import React, { useState} from 'react';
import {
 Phone, Mail, MessageCircle, Clock, ChevronDown, HeadphonesIcon,
 Send, ExternalLink, CheckCircle, HelpCircle, Zap, BookOpen,
 ArrowRight, Sparkles, Loader, AlertCircle, Shield, Globe, Activity,
 ChevronRight, Smartphone, Info, ShieldCheck, Heart, Database, Cpu, User,
 RefreshCw, Headphones
} from 'lucide-react';
import api from '../services/api';
import { Card} from '../components/ui/Card';
import { Button} from '../components/ui/Button';
import { Badge} from '../components/ui/Badge';
import { Input} from '../components/ui/Input';
import alertService from '../utils/alert';
import { cn} from '../utils/cn';
import PageHeader from '../components/PageHeader';

const FAQS = [
 {
 q: 'How do I connect my WhatsApp account?',
 a: 'Navigate to the Devices page, click "Add Device", and scan the generated QR code using WhatsApp > Linked Devices on your mobile device.',
 icon: Phone,
},
 {
 q: 'Troubleshooting duplicate messages',
 a: 'Duplicate messages are often caused by browser session conflicts or unstable network synchronization. Ensure you are running the latest version and refresh your device connection.',
 icon: Zap,
},
 {
 q: 'Upgrading my subscription plan',
 a: 'Go to Subscription > Billing, select your desired tier, and complete the secure payment. Your account will be upgraded instantly.',
 icon: Sparkles,
},
 {
 q: 'Sending media files in bulk',
 a: 'You can attach images, PDFs, and videos directly within the Messaging campaign setup. Media is automatically optimized for delivery.',
 icon: Send,
},
 {
 q: 'Data Export and GDPR',
 a: 'You can request a full archive of your communication logs and account data from the Settings > Privacy section at any time.',
 icon: BookOpen,
},
 {
 q: 'Message sending rate limits',
 a: 'Standard accounts have a throttled delivery rate to ensure account safety. High-volume accounts can request limit increases based on use-case validation.',
 icon: Clock,
},
];

const CONTACTS = [
 {
 icon: Headphones,
 color: 'text-[#2E844A]',
 title: 'Phone Support',
 value: '+91 73920 80372',
 href: 'tel:+917392080372',
 sub: 'Mon-Sat, 10:00 - 19:00 IST',
},
 {
 icon: Mail,
 color: 'text-[#0176D3]',
 title: 'Electronic Dispatch',
 value: 'info@waapcoders.in',
 href: 'mailto:info@waapcoders.in',
 sub: 'Latency: < 4 Hours',
},
 {
 icon: MessageCircle,
 color: 'text-[#0176D3]',
 title: 'Real-time Support',
 value: 'WhatsApp Channel',
 href: 'https://wa.me/917392080372',
 external: true,
 sub: 'Priority assistance',
},
 {
 icon: Globe,
 color: 'text-[#DD7A01]',
 title: 'Global Coverage',
 value: 'IST Timeframe',
 sub: 'Available 10:00 - 19:00',
},
];

function FAQItem({ q, a, icon: Icon}) {
 const [open, setOpen] = useState(false);
 return (
 <Card className={cn(
 "overflow-hidden transition-all duration-500 bg-white border-[#D8DDE6] .03] group rounded-lg p-0 shadow-sm",
 open ? "border-[#0176D3]/30 _0_80px_rgba(0,0,0,0.5)] scale-[1.01] z-10" : "hover:border-[#D8DDE6]"
 )}>
 <button className="w-full flex items-center justify-between p-4 sm:p-5 text-left" onClick={() => setOpen(!open)}>
 <div className="flex items-center gap-6">
 <div className={cn(
 "w-12 h-12 rounded-lg flex items-center justify-center transition-all shadow-inner",
 open ? "bg-[#0176D3] text-white" : "bg-[#F4F6F9] text-[#706E6B] "
 )}>
 <Icon size={22} strokeWidth={1.5} />
 </div>
 <span className="text-[13px] font-bold text-[#080707] tracking-tight leading-none ">{q}</span>
 </div>
 <ChevronDown size={20} className={cn("text-[#706E6B] transition-transform duration-500", open && "rotate-180 text-[#0176D3]")} />
 </button>
 <div className={cn(
 "grid transition-all duration-500 ease-in-out px-8",
 open ? "grid-rows-[1fr] opacity-100 pb-10 pt-4 border-t border-[#D8DDE6] .03]" : "grid-rows-[0fr] opacity-0"
 )}>
 <div className="overflow-hidden">
 <p className="pl-12 text-[12px] text-[#706E6B] font-bold leading-relaxed border-l-2 border-[#0176D3]/30 py-3 ">
 {a}
 </p>
 </div>
 </div>
 </Card>
 );
}

export default function SupportPage() {
 const [form, setForm] = useState({ name: '', email: '', subject: '', message: ''});
 const [submitted, setSubmitted] = useState(false);
 const [sending, setSending] = useState(false);

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!form.name || !form.email || !form.message) {
 alertService.error('Validation Error', 'Please complete all required fields.');
 return;
}
 
 setSending(true);
 try {
 const res = await api.post('/public/contact', form);
 if (res.data.success) {
 setSubmitted(true);
 alertService.toast('success', 'Diagnostic inquiry successfully transmitted');
 setForm({ name: '', email: '', subject: '', message: ''});
}
} catch (err) {
 alertService.error('System Error', err.response?.data?.error || 'Failed to connect to support nodes.');
} finally {
 setSending(false);
}
};

 return (
 <div className="w-full pb-10">
 <PageHeader title="Support" icon={Headphones} subtitle="Contact our support team or browse the knowledge base." />

 {/* Contact Channels Grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
 {CONTACTS.map((c, i) => (
 <a 
 key={i} 
 href={c.href} 
 target={c.external ? '_blank' : undefined} 
 rel={c.external ? 'noreferrer' : undefined}
 className="group"
 >
 <Card className="p-4 sm:p-5 bg-white border-[#E5E5E5] hover:bg-[#F4F6F9] hover:border-[#0176D3]/30 transition-all duration-700 shadow-[0_1px_3px_rgba(0,0,0,0.08)] relative overflow-hidden h-full rounded-lg group/card">
 <div className="flex items-start justify-between mb-6">
 <div className={cn(
 "w-14 h-14 rounded-lg bg-[#F3F2F2] border border-[#D8DDE6] flex items-center justify-center shadow-inner group-hover:rotate-12 transition-transform duration-700 ring-1 ring-white/5",
 c.color
 )}>
 <c.icon size={26} strokeWidth={1.5} />
 </div>
 <div className="w-10 h-10 rounded-lg bg-[#E5E5E5] flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all shadow-inner border border-[#D8DDE6] ">
 {c.external ? <ExternalLink size={14} className="text-[#0176D3]" /> : <ChevronRight size={14} className="text-[#0176D3]" />}
 </div>
 </div>
 <div className="space-y-4 relative z-10 text-left">
 <div className="space-y-1">
 <div className="text-[11px] font-bold text-[#706E6B] leading-none">{c.title}</div>
 <div className="text-[18px] font-bold text-[#080707] tracking-tight truncate leading-none group-hover/card:text-[#0176D3] transition-colors">{c.value}</div>
 </div>
 <div className="flex items-center gap-2 pt-4 border-t border-[#D8DDE6]">
 <div className="w-1.5 h-1.5 rounded-full bg-[#0176D3] animate-pulse" />
 <div className="text-[11px] font-bold text-[#706E6B] leading-none">{c.sub}</div>
 </div>
 </div>
 </Card>
 </a>
 ))}
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
 
 {/* Support Ticket Form */}
 <Card className="lg:col-span-7 bg-white border-[#E5E5E5] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.08)] _0_80px_rgba(0,0,0,0.5)] p-0 rounded-lg">
 <div className="px-12 py-10 border-b border-[#D8DDE6] flex items-center gap-4 bg-[#F4F6F9] ">
 <div className="w-16 h-16 rounded-[1.8rem] bg-[#0176D3]/10 border border-[#0176D3]/20 flex items-center justify-center text-[#0176D3] shadow-sm">
 <Send size={32} strokeWidth={1} />
 </div>
 <div className="space-y-2">
 <h2 className="text-2xl font-bold text-[#080707] tracking-tighter leading-none">Diagnostic Inquiry</h2>
 <div className="flex items-center gap-3">
 <div className="w-1.5 h-1.5 rounded-full bg-[#0176D3] " />
 <p className="text-[11px] font-bold text-[#706E6B] leading-none ">Establish a direct link with Technical Oversight Department.</p>
 </div>
 </div>
 </div>

 <div className="p-4 sm:p-5">
 {submitted ? (
 <div className="py-24 text-center space-y-12 animate-nebula-fade-up">
 <div className="w-28 h-28 mx-auto rounded-[3.5rem] bg-[#2E844A]/10 border border-[#2E844A]/20 flex items-center justify-center text-[#2E844A] shadow-sm shadow-emerald-500/20 shadow-inner">
 <CheckCircle size={56} strokeWidth={1} />
 </div>
 <div className="space-y-4">
 <h3 className="text-3xl font-bold text-[#080707] tracking-tighter leading-none">Ticket Submitted</h3>
 <p className="text-[11px] font-bold text-[#706E6B] max-w-sm mx-auto leading-relaxed ">
 Your inquiry has been queued. Feedback will be dispatched via your registered communications uplink within 4 hours.
 </p>
 </div>
 <Button variant="outline" className="h-16 px-4 font-bold text-[11px] rounded-lg border-[#D8DDE6] " icon={RefreshCw} onClick={() => setSubmitted(false)}>
 New Ticket
 </Button>
 </div>
 ) : (
 <form className="space-y-12" onSubmit={handleSubmit}>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-3">
 <label className="text-[12px] font-semibold text-[#706E6B] ml-1 ">Account Identity</label>
 <Input 
 icon={User} 
 value={form.name} 
 onChange={e => setForm({...form, name: e.target.value})} 
 placeholder="Your legal name" 
 required 
 disabled={sending} 
 className="h-16 rounded-lg bg-[#F4F6F9] border-[#D8DDE6] shadow-inner font-bold text-[12px] "
 />
 </div>
 <div className="space-y-3">
 <label className="text-[12px] font-semibold text-[#706E6B] ml-1 ">Send Feedback</label>
 <Input 
 type="email" 
 icon={Mail} 
 value={form.email} 
 onChange={e => setForm({...form, email: e.target.value})} 
 placeholder="your@email.com" 
 required 
 disabled={sending} 
 className="h-16 rounded-lg bg-[#F4F6F9] border-[#D8DDE6] shadow-inner text-[12px]"
 />
 </div>
 </div>
 <div className="space-y-3">
 <label className="text-[12px] font-semibold text-[#706E6B] ml-1 ">Subject Context</label>
 <Input 
 icon={Info} 
 value={form.subject} 
 onChange={e => setForm({...form, subject: e.target.value})} 
 placeholder="Define the technical context…" 
 required 
 disabled={sending} 
 className="h-16 rounded-lg bg-[#F4F6F9] border-[#D8DDE6] shadow-inner font-bold text-[12px] "
 />
 </div>
 <div className="space-y-4">
 <label className="text-[12px] font-semibold text-[#706E6B] ml-1 ">Message</label>
 <div className="relative rounded-lg overflow-hidden border border-[#D8DDE6] bg-[#F4F6F9] focus-within:border-[#0176D3]/30 transition-all shadow-inner ring-1 ring-white/[0.02]">
 <textarea 
 className="w-full px-4 py-10 min-h-[220px] bg-transparent text-[#080707] text-[14px] leading-relaxed resize-none focus:outline-none scrollbar-hide focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" 
 value={form.message} 
 onChange={e => setForm({...form, message: e.target.value})} 
 placeholder="DESCRIBE_ISSUE_HERE…" 
 required 
 disabled={sending} 
 />
 </div>
 </div>
 <Button type="submit" variant="primary" size="lg" className="h-20 w-full font-bold shadow-sm shadow-blue-600/30 rounded-lg" icon={Zap} loading={sending}>
 Submit Ticket
 </Button>
 </form>
 )}
 </div>
 </Card>
 
 {/* FAQs & Sidebar Info */}
 <div className="lg:col-span-5 space-y-12">
 <div className="space-y-10">
 <div className="flex items-center gap-6 px-4">
 <div className="w-14 h-9 rounded-lg bg-[#0176D3]/10 border border-[#0176D3]/20 flex items-center justify-center text-[#0176D3] shadow-sm">
 <HelpCircle size={28} strokeWidth={1.5} />
 </div>
 <div className="space-y-1 text-left">
 <h2 className="text-2xl font-bold text-[#080707] tracking-tighter leading-none">Knowledge Bank</h2>
 <div className="flex items-center gap-3">
 <div className="w-1.5 h-1.5 rounded-full bg-[#0176D3] " />
 <p className="text-[11px] font-bold text-[#706E6B] leading-none ">Automated archives</p>
 </div>
 </div>
 </div>
 
 <div className="space-y-6">
 {FAQS.map((faq, i) => <FAQItem key={i} {...faq} />)}
 </div>
 </div>

 <Card className="p-4 sm:p-5 bg-[#F4F6F9] border-[#E5E5E5] shadow-inner rounded-lg space-y-10">
 <div className="flex items-center gap-5 text-[11px] font-bold text-[#706E6B] leading-none">
 <Activity size={20} className="text-[#2E844A]" /> Operational Status
 </div>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 {[
 { label: 'SaaS Nodes', status: 'Optimal', icon: Globe, color: 'text-[#0176D3]'},
 { label: 'Cloud Link', status: 'Active', icon: Cpu, color: 'text-[#0176D3]'}
].map((s, i) => (
 <div key={i} className="p-6 rounded-lg bg-white border border-[#E5E5E5] space-y-4 group overflow-hidden relative transition-all hover:bg-[#F4F6F9] shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
 <div className="flex items-center justify-between">
 <div className={cn(s.color)}><s.icon size={18} strokeWidth={1.5} /></div>
 <div className="flex items-center gap-2">
 <div className="w-1.5 h-1.5 rounded-full bg-[#2E844A] animate-pulse" />
 </div>
 </div>
 <div className="space-y-1 text-left">
 <div className="text-[11px] font-bold text-[#706E6B] leading-none ">{s.label}</div>
 <div className="text-[11px] font-bold text-[#2E844A] leading-none">{s.status}</div>
 </div>
 </div>
 ))}
 </div>
 <div className="pt-10 border-t border-[#D8DDE6] flex items-center justify-center gap-4 text-[#706E6B] ">
 <ShieldCheck size={18} strokeWidth={1} className="text-[#0176D3]" />
 <span className="text-[11px] font-bold ">Platform Security Verified</span>
 </div>
 </Card>
 </div>

 </div>
 </div>
 );
}
